import { useEffect, useRef, useState, type FC, type FormEvent, type MouseEvent } from 'react'

import { BodyText } from '@/components/core'
import { CloseIcon } from '@/components/core/Icon/icons'
import { CREATE_ORGANIZATION_MODAL_TEST_IDS } from '@/constants/testIds'

interface CreateOrganizationModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (name: string) => void
  isLoading?: boolean
  error?: string | null
}

export const CreateOrganizationModal: FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
}) => {
  const [organizationName, setOrganizationName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOrganizationName('')
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const trimmedName = organizationName.trim()
    if (trimmedName) {
      onSubmit(trimmedName)
    }
  }

  const handleOverlayClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="create-org-modal"
      onClick={handleOverlayClick}
      data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.OVERLAY}
    >
      <div
        className="create-org-modal__content"
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.ROOT}
      >
        <button
          className="create-org-modal__close"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
          data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.CLOSE_BUTTON}
        >
          <CloseIcon className="create-org-modal__close-icon" />
        </button>

        <form onSubmit={handleSubmit} className="create-org-modal__form">
          <div className="create-org-modal__header">
            <h2 className="create-org-modal__title">Create new workspace</h2>
            <BodyText className="create-org-modal__description">
              After creating a new workspace you will be redirected to a new space. You can switch back to your previous
              workspace.
            </BodyText>
          </div>

          <div className="create-org-modal__body">
            <input
              ref={inputRef}
              type="text"
              className="create-org-modal__input"
              placeholder="New workspace 1"
              value={organizationName}
              onChange={e => setOrganizationName(e.target.value)}
              disabled={isLoading}
              required
              maxLength={100}
              data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.NAME_INPUT}
            />
            {error && (
              <BodyText className="create-org-modal__error" data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.ERROR}>
                {error}
              </BodyText>
            )}
          </div>

          <div className="create-org-modal__footer">
            <button
              type="button"
              className="create-org-modal__button create-org-modal__button--cancel"
              onClick={onClose}
              disabled={isLoading}
              data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.CANCEL_BUTTON}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-org-modal__button create-org-modal__button--primary"
              disabled={isLoading || !organizationName.trim()}
              data-testid={CREATE_ORGANIZATION_MODAL_TEST_IDS.SUBMIT_BUTTON}
            >
              {isLoading ? 'Creating...' : 'Create workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
